/**
 * Le parole del piano editoriale (document/63). Tenute fuori da dict.ts per
 * non allungarlo: vengono distribuite nei due alberi, quindi `dict.test.ts` le
 * controlla come tutte le altre (stesse chiavi in italiano e in inglese).
 */
import type { DictNode } from './dict.js'

export const editorialIt: DictNode = {
  editorialPlan: {
    nav: 'Piano editoriale',
    title: 'Piano editoriale',
    subtitle: 'Gli articoli che il sito vuole scrivere, alimentati dalle schede del tool SEO: dalla proposta alla pubblicazione.',
    back: 'Piano editoriale',
    loadError: 'Non riesco a leggere il piano editoriale',
    filters: 'Filtri del piano',
    filterAll: 'Tutte',
    search: 'Cerca per titolo o parola chiave',
    searchButton: 'Cerca',
    viewList: 'Vista elenco',
    viewBoard: 'Vista per stato',
    columnEmpty: 'Nessuna voce in questo stato.',
    noMatch: 'Nessuna voce corrisponde',
    noMatchHelp: 'Cambia la ricerca o il filtro per stato.',
    noKeyword: 'Nessuna parola chiave',
    emptyValue: '—',
    headingsCount: '{{count}} H2',
    assigned: 'Assegnata',
    originSeo: 'Dal tool SEO',
    originManual: 'Scritta a mano',
    originSeoCard: 'Scheda SEO {{id}}',
    late: 'In ritardo: {{date}}',
    lateHelp: 'La data prevista è passata e l’articolo non è ancora online. Sposta la data o chiudi la voce.',
    seoNotNotified: 'Il tool SEO non è stato avvisato',
    seoNotNotifiedHelp: 'L’articolo è pubblicato qui, ma il tool SEO non ha ricevuto l’indirizzo: senza, non misura la pagina. Riprova quando il tool risponde.',
    retryNotify: 'Riprova l’avviso',
    conflict: 'Il piano è cambiato nel frattempo: l’ho riletto, ripeti l’operazione.',
    status: {
      proposal: 'Proposta',
      approved: 'Approvata',
      writing: 'In scrittura',
      review: 'In revisione',
      published: 'Pubblicata',
      discarded: 'Scartata',
    },
    signal: {
      volume: '{{count}} ricerche/mese',
      difficulty: 'Difficoltà {{value}}',
      position: 'Posizione {{value}}',
    },
    difficulty: { low: 'bassa', medium: 'media', high: 'alta' },
    empty: {
      title: 'Il piano è vuoto',
      help: 'Importa le schede dal tool SEO, incolla il JSON di una scheda o aggiungi una voce a mano. Ogni voce parte come proposta e si approva solo con parola chiave principale e H2.',
    },
    cannibalization: {
      title: 'Possibile cannibalizzazione',
      help: 'Un’altra voce attiva punta alla stessa parola chiave nella stessa lingua: due pagine si contenderebbero la stessa ricerca. È un avviso, non un blocco.',
      short: '{{count}} voci sulla stessa ricerca',
    },
    field: {
      slug: 'Slug',
      title: 'Titolo',
      metaTitle: 'Titolo per i motori (meta title)',
      metaDescription: 'Descrizione per i motori (meta description)',
      mainKeyword: 'Parola chiave principale',
      searchIntent: 'Intento di ricerca',
      audience: 'Pubblico',
      persona: 'Persona',
      funnelStage: 'Fase del funnel',
      monthlySearches: 'Ricerche al mese',
      keywordDifficulty: 'Difficoltà della parola chiave',
      cpcLow: 'CPC minimo',
      cpcHigh: 'CPC massimo',
      cpc: 'Costo per clic',
      currentPosition: 'Posizione attuale',
      headings: 'Struttura (H2)',
      keywords: 'Parole chiave da contenere',
      relatedKeywords: 'Parole chiave correlate',
      keyword: 'Parola chiave',
      angle: 'Angolo',
      outline: 'Scaletta',
      h1: 'H1',
      opening: 'Apertura',
      closing: 'Chiusura',
      tone: 'Tono',
      avoid: 'Da evitare',
      length: 'Lunghezza',
      proof: 'Prove e dati',
      goal: 'Obiettivo',
      cta: 'Invito all’azione (CTA)',
      internalLinks: 'Link interni suggeriti',
      pillarUrl: 'Pagina pilastro',
      coverPrompt: 'Prompt dell’immagine di copertina',
      images: 'Immagini',
      writingPrompt: 'Prompt di scrittura',
      locales: 'Lingue',
      researchId: 'Ricerca di partenza',
      seoPriority: 'Priorità nel tool SEO',
      seoStatus: 'Stato nel tool SEO',
      locale: 'Lingua',
      notes: 'Note',
      priority: 'Priorità (1 è la prima)',
      scheduledFor: 'Data prevista',
      assignee: 'Assegnata a',
    },
    schedule: {
      title: 'Voci del piano editoriale',
      help: 'Articoli con una data prevista che non sono ancora online: sono intenzioni, non pubblicazioni.',
    },
    imageN: 'Immagine {{n}}',
    section: {
      structure: 'Struttura dell’articolo',
      structureHelp: 'Gli H2 con la parola chiave a cui rispondono e l’angolo da dare.',
      card: 'Scheda dell’articolo',
      links: 'Link interni e pilastro',
      images: 'Copertina e immagini',
      imagesHelp: 'Sono prompt conservati: le immagini non si generano da sole.',
      prompt: 'Prompt di scrittura',
      signals: 'Segnali di ricerca',
      schedule: 'Calendario e assegnazione',
      link: 'Collegamento',
      results: 'Risultati dopo la pubblicazione',
      seoUpdate: 'Aggiornamento dal tool SEO',
    },
    noHeadings: 'La scheda non ha ancora H2.',
    noLinks: 'Nessun link interno suggerito.',
    noImages: 'Nessuna immagine nella scheda.',
    promptShow: 'Mostra il prompt ({{count}} caratteri)',
    localEdits: 'Hai modificato {{count}} campi della scheda qui: un aggiornamento dal tool SEO non li sovrascrive.',
    scheduleHelp: 'La voce compare nel calendario del sito alla data scelta.',
    scheduleSave: 'Salva la data',
    scheduleClear: 'Togli la data',
    assignSave: 'Assegna',
    unassigned: 'Nessuno',
    assigneeIdHelp: 'Incolla l’id della persona. Chi gestisce le persone può sceglierla da un elenco.',
    openContent: 'Apri la bozza',
    openPage: 'Apri la pagina',
    openPublished: 'Apri l’articolo online',
    noLink: 'Nessuna bozza collegata: si crea da «Crea la bozza» quando la voce è approvata.',
    publishedOn: 'Pubblicata il {{date}}',
    refreshResults: 'Aggiorna i risultati',
    results: {
      grade: 'Voto',
      position: 'Posizione',
      impressions: 'Impressioni',
      clicks: 'Clic',
      captured: 'Letti dal tool SEO il {{date}}',
      none: 'Nessun risultato ancora. Il tool SEO li misura dopo la pubblicazione: usa «Aggiorna i risultati».',
    },
    seoUpdateHelp: 'Rilegge la scheda dal tool SEO e mostra cosa cambierebbe. I campi che hai modificato qui restano come sono.',
    seoUpdateCheck: 'Controlla aggiornamenti',
    seoUpdate: {
      title: 'Aggiornamento dal tool SEO',
      apply: 'Applica le differenze',
      failed: 'Non riesco a leggere la scheda',
      upToDate: 'La voce è già aggiornata all’ultima versione della scheda.',
    },
    approveBlockedTitle: 'Questa voce non si può ancora approvare',
    approveBlockedHelp: 'Completa la scheda con «Modifica» e riprova.',
    discardedTitle: 'Voce scartata',
    action: {
      approve: 'Approva',
      'return-to-proposal': 'Torna a proposta',
      'start-writing': 'Segna in scrittura',
      'to-review': 'Manda in revisione',
      'back-to-writing': 'Torna in scrittura',
      discard: 'Scarta',
      reopen: 'Riapri come proposta',
      draft: 'Crea la bozza',
      publish: 'Segna come pubblicata',
    },
    done: {
      approve: 'Voce approvata.',
      'return-to-proposal': 'Voce tornata a proposta.',
      'start-writing': 'Voce in scrittura.',
      'to-review': 'Voce mandata in revisione.',
      'back-to-writing': 'Voce tornata in scrittura.',
      discard: 'Voce scartata.',
      reopen: 'Voce riaperta come proposta.',
      schedule: 'Data salvata.',
      unschedule: 'Data tolta.',
      assign: 'Assegnazione salvata.',
      edit: 'Modifiche salvate.',
      draft: 'Bozza creata e collegata alla voce.',
      publish: 'Voce segnata come pubblicata.',
      publishedSeo: 'Voce pubblicata e tool SEO avvisato.',
      publishedNoSeo: 'Voce pubblicata, ma il tool SEO non è stato avvisato.',
      performance: 'Risultati aggiornati.',
    },
    reason: {
      approve: 'Approvata nel piano editoriale: {{title}}',
      'return-to-proposal': 'Approvazione ritirata: {{title}}',
      'start-writing': 'Inizia la scrittura: {{title}}',
      'to-review': 'Mandata in revisione: {{title}}',
      'back-to-writing': 'Rimandata in scrittura: {{title}}',
      discard: 'Scartata dal piano editoriale: {{title}}',
      reopen: 'Riaperta come proposta: {{title}}',
      schedule: 'Data prevista aggiornata: {{title}}',
      assign: 'Assegnazione aggiornata: {{title}}',
      edit: 'Scheda modificata nel piano editoriale: {{title}}',
      draft: 'Bozza creata dal piano editoriale: {{title}}',
      publish: 'Pubblicata: {{title}}',
      performance: 'Risultati letti dal tool SEO: {{title}}',
      seoUpdate: 'Aggiornamento dalla scheda del tool SEO: {{title}}',
      add: 'Voce aggiunta a mano al piano editoriale: {{title}}',
      import: 'Schede importate nel piano editoriale',
      bridgeOn: 'Ponte con il tool SEO acceso',
      bridgeOff: 'Ponte con il tool SEO spento',
    },
    edit: {
      button: 'Modifica',
      title: 'Modifica la voce',
      save: 'Salva le modifiche',
      seoHelp: 'I campi che modifichi qui vincono su una versione futura della scheda del tool SEO.',
      headingsHelp: 'Un H2 per riga: titolo | parola chiave | angolo. Parola chiave e angolo sono facoltativi.',
      keywordsHelp: 'Separate da virgola o da a capo.',
      priorityHelp: '1 è la prima da scrivere, 999 l’ultima.',
      priorityInvalid: 'Scrivi un numero intero da 1 a 999.',
    },
    new: {
      button: 'Nuova voce',
      title: 'Nuova voce nel piano',
      titleHelp: 'Il titolo di lavoro dell’articolo.',
      titleMissing: 'Scrivi il titolo.',
      keywordHelp: 'Serve per approvare e per avvisarti se un’altra voce punta alla stessa ricerca.',
      create: 'Aggiungi al piano',
      failed: 'La voce non è stata aggiunta',
    },
    discard: {
      title: 'Scartare questa voce?',
      help: 'La voce esce dal piano attivo e non conta più per la cannibalizzazione. Puoi riaprirla quando vuoi.',
      note: 'Perché la scarti (facoltativo)',
      confirm: 'Scarta la voce',
    },
    publish: {
      title: 'Segna come pubblicata',
      help: 'La bozza collegata deve essere già pubblicata. Il CMS comunica l’indirizzo al tool SEO, che da quel giorno misura la pagina. Se il tool non risponde la voce resta pubblicata e puoi ripetere l’avviso.',
      url: 'Indirizzo dell’articolo online',
      urlInvalid: 'Scrivi un indirizzo completo che inizia con https://.',
      urlNeeded: 'La voce non ha una bozza collegata: scrivi dove è online l’articolo.',
      urlOptional: 'Lascia vuoto per usare l’indirizzo della bozza collegata.',
      confirm: 'Conferma',
    },
    bridge: {
      open: 'Ponte con il tool SEO',
      title: 'Ponte con il tool SEO',
      help: 'Il tool SEO non distingue i clienti: il ponte si accende sito per sito, con un segreto del gateway dedicato. Spento, il piano funziona lo stesso con il JSON incollato.',
      offTitle: 'Il ponte con il tool SEO è spento',
      offHelp: 'Puoi importare le schede incollando il loro JSON. Chi amministra il cliente accende il ponte dalle impostazioni.',
      readOnly: 'Solo chi amministra il cliente può cambiare queste impostazioni.',
      failed: 'Impostazioni non salvate',
      enabled: 'Ponte acceso per questo sito',
      baseUrl: 'Indirizzo del tool SEO',
      baseUrlHelp: 'Solo https e solo un indirizzo di miglioresoftware.com: il token non viene mai inviato altrove.',
      seoDomain: 'Dominio del sito nel tool SEO',
      seoDomainHelp: 'Come questo sito si chiama nel tool SEO, per esempio example.com.',
      tokenRef: 'Nome del segreto con il token',
      tokenRefHelp: 'Il NOME del segreto del gateway, per esempio SEO_TOKEN_ACME. Mai il token: lo imposta chi gestisce il gateway.',
      tokenRefInvalid: 'Il nome inizia con SEO_TOKEN_ e usa solo lettere maiuscole, numeri e trattino basso.',
      tokenOk: 'Il segreto è configurato nel gateway.',
      tokenMissing: 'Il segreto {{name}} non è ancora impostato nel gateway.',
      save: 'Salva',
    },
    import: {
      button: 'Importa dal tool SEO',
      pasteJson: 'Incolla un JSON',
      title: 'Importa schede nel piano',
      source: 'Da dove importare',
      fromSeo: 'Dal tool SEO',
      fromJson: 'Incolla un JSON',
      preview: 'Anteprima',
      run: 'Importa',
      failed: 'Importazione non riuscita',
      queueError: 'Il tool SEO non risponde',
      useJsonInstead: 'Intanto puoi incollare il JSON di una scheda.',
      queueEmpty: 'Nessuna scheda in coda',
      queueEmptyHelp: 'Il tool SEO non ha schede in attesa di essere scritte.',
      selectNew: 'Seleziona le nuove e quelle da aggiornare',
      inPlan: 'Già nel piano',
      behind: 'Nel piano, da aggiornare',
      noResearch: 'Senza dati di ricerca',
      invalid: 'Scheda non valida',
      jsonLabel: 'JSON della scheda (o di un elenco di schede)',
      jsonHelp: 'Incolla il JSON di una scheda come lo esporta il tool SEO. Serve il campo research_id: senza, la scheda non è stata decisa su dati di ricerca recenti e viene rifiutata.',
      paste: {
        empty: 'Incolla un JSON.',
        tooLarge: 'Il testo è troppo grande: al massimo 2 MB.',
        notJson: 'Questo non è un JSON valido.',
        notCards: 'Il JSON deve essere una scheda (un oggetto) o un elenco di schede.',
        tooMany: 'Al massimo 100 schede per volta.',
      },
      reportTitle: 'Esito dell’importazione',
      previewTitle: 'Anteprima: non è stato cambiato niente',
      doneTitle: 'Importazione completata',
      summary: '{{created}} nuove, {{updated}} aggiornate, {{unchanged}} già aggiornate, {{skipped}} ignorate, {{rejected}} rifiutate.',
      keptLocal: 'resta il tuo valore',
      keptLocalHelp: 'Il tool SEO ha cambiato questi campi, ma li avevi già modificati qui: restano i tuoi.',
      outcome: {
        created: 'Nuova',
        updated: 'Aggiornata',
        unchanged: 'Già aggiornata',
        skipped: 'Ignorata',
        rejected: 'Rifiutata',
      },
    },
  },
}

export const editorialEn: DictNode = {
  editorialPlan: {
    nav: 'Editorial plan',
    title: 'Editorial plan',
    subtitle: 'The articles the site means to write, fed by the SEO tool cards: from proposal to publication.',
    back: 'Editorial plan',
    loadError: 'Cannot read the editorial plan',
    filters: 'Plan filters',
    filterAll: 'All',
    search: 'Search by title or keyword',
    searchButton: 'Search',
    viewList: 'List view',
    viewBoard: 'Board by status',
    columnEmpty: 'No item in this status.',
    noMatch: 'No item matches',
    noMatchHelp: 'Change the search or the status filter.',
    noKeyword: 'No keyword',
    emptyValue: '—',
    headingsCount: '{{count}} H2',
    assigned: 'Assigned',
    originSeo: 'From the SEO tool',
    originManual: 'Written by hand',
    originSeoCard: 'SEO card {{id}}',
    late: 'Late: {{date}}',
    lateHelp: 'The planned date has passed and the article is not online yet. Move the date or close the item.',
    seoNotNotified: 'The SEO tool was not told',
    seoNotNotifiedHelp: 'The article is published here, but the SEO tool did not receive the address: without it, it cannot measure the page. Try again when the tool answers.',
    retryNotify: 'Try the notice again',
    conflict: 'The plan changed in the meantime: I read it again, repeat the operation.',
    status: {
      proposal: 'Proposal',
      approved: 'Approved',
      writing: 'Writing',
      review: 'In review',
      published: 'Published',
      discarded: 'Discarded',
    },
    signal: {
      volume: '{{count}} searches/month',
      difficulty: 'Difficulty {{value}}',
      position: 'Position {{value}}',
    },
    difficulty: { low: 'low', medium: 'medium', high: 'high' },
    empty: {
      title: 'The plan is empty',
      help: 'Import the cards from the SEO tool, paste the JSON of a card or add an item by hand. Every item starts as a proposal and is approved only with a main keyword and H2.',
    },
    cannibalization: {
      title: 'Possible cannibalization',
      help: 'Another active item targets the same keyword in the same language: two pages would compete for the same search. It is a warning, not a block.',
      short: '{{count}} items on the same search',
    },
    field: {
      slug: 'Slug',
      title: 'Title',
      metaTitle: 'Search engine title (meta title)',
      metaDescription: 'Search engine description (meta description)',
      mainKeyword: 'Main keyword',
      searchIntent: 'Search intent',
      audience: 'Audience',
      persona: 'Persona',
      funnelStage: 'Funnel stage',
      monthlySearches: 'Searches per month',
      keywordDifficulty: 'Keyword difficulty',
      cpcLow: 'Minimum CPC',
      cpcHigh: 'Maximum CPC',
      cpc: 'Cost per click',
      currentPosition: 'Current position',
      headings: 'Structure (H2)',
      keywords: 'Keywords to include',
      relatedKeywords: 'Related keywords',
      keyword: 'Keyword',
      angle: 'Angle',
      outline: 'Outline',
      h1: 'H1',
      opening: 'Opening',
      closing: 'Closing',
      tone: 'Tone',
      avoid: 'To avoid',
      length: 'Length',
      proof: 'Proof and data',
      goal: 'Goal',
      cta: 'Call to action (CTA)',
      internalLinks: 'Suggested internal links',
      pillarUrl: 'Pillar page',
      coverPrompt: 'Cover image prompt',
      images: 'Images',
      writingPrompt: 'Writing prompt',
      locales: 'Languages',
      researchId: 'Starting research',
      seoPriority: 'Priority in the SEO tool',
      seoStatus: 'Status in the SEO tool',
      locale: 'Language',
      notes: 'Notes',
      priority: 'Priority (1 is first)',
      scheduledFor: 'Planned date',
      assignee: 'Assigned to',
    },
    schedule: {
      title: 'Editorial plan items',
      help: 'Articles with a planned date that are not online yet: they are intentions, not publications.',
    },
    imageN: 'Image {{n}}',
    section: {
      structure: 'Article structure',
      structureHelp: 'The H2 with the keyword each answers and the angle to take.',
      card: 'Article card',
      links: 'Internal links and pillar',
      images: 'Cover and images',
      imagesHelp: 'These are kept prompts: images are never generated on their own.',
      prompt: 'Writing prompt',
      signals: 'Search signals',
      schedule: 'Calendar and assignment',
      link: 'Link',
      results: 'Results after publication',
      seoUpdate: 'Update from the SEO tool',
    },
    noHeadings: 'The card has no H2 yet.',
    noLinks: 'No internal link suggested.',
    noImages: 'No image in the card.',
    promptShow: 'Show the prompt ({{count}} characters)',
    localEdits: 'You edited {{count}} fields of the card here: an update from the SEO tool does not overwrite them.',
    scheduleHelp: 'The item shows in the site calendar on the chosen date.',
    scheduleSave: 'Save the date',
    scheduleClear: 'Clear the date',
    assignSave: 'Assign',
    unassigned: 'Nobody',
    assigneeIdHelp: 'Paste the id of the person. Whoever manages people can pick from a list.',
    openContent: 'Open the draft',
    openPage: 'Open the page',
    openPublished: 'Open the live article',
    noLink: 'No draft linked: create it with “Create the draft” once the item is approved.',
    publishedOn: 'Published on {{date}}',
    refreshResults: 'Refresh results',
    results: {
      grade: 'Grade',
      position: 'Position',
      impressions: 'Impressions',
      clicks: 'Clicks',
      captured: 'Read from the SEO tool on {{date}}',
      none: 'No results yet. The SEO tool measures them after publication: use “Refresh results”.',
    },
    seoUpdateHelp: 'Reads the card again from the SEO tool and shows what would change. Fields you edited here stay as they are.',
    seoUpdateCheck: 'Check for updates',
    seoUpdate: {
      title: 'Update from the SEO tool',
      apply: 'Apply the differences',
      failed: 'Cannot read the card',
      upToDate: 'The item is already at the latest version of the card.',
    },
    approveBlockedTitle: 'This item cannot be approved yet',
    approveBlockedHelp: 'Complete the card with “Edit” and try again.',
    discardedTitle: 'Discarded item',
    action: {
      approve: 'Approve',
      'return-to-proposal': 'Back to proposal',
      'start-writing': 'Mark as writing',
      'to-review': 'Send to review',
      'back-to-writing': 'Back to writing',
      discard: 'Discard',
      reopen: 'Reopen as proposal',
      draft: 'Create the draft',
      publish: 'Mark as published',
    },
    done: {
      approve: 'Item approved.',
      'return-to-proposal': 'Item back to proposal.',
      'start-writing': 'Item in writing.',
      'to-review': 'Item sent to review.',
      'back-to-writing': 'Item back to writing.',
      discard: 'Item discarded.',
      reopen: 'Item reopened as a proposal.',
      schedule: 'Date saved.',
      unschedule: 'Date cleared.',
      assign: 'Assignment saved.',
      edit: 'Changes saved.',
      draft: 'Draft created and linked to the item.',
      publish: 'Item marked as published.',
      publishedSeo: 'Item published and the SEO tool told.',
      publishedNoSeo: 'Item published, but the SEO tool was not told.',
      performance: 'Results refreshed.',
    },
    reason: {
      approve: 'Approved in the editorial plan: {{title}}',
      'return-to-proposal': 'Approval taken back: {{title}}',
      'start-writing': 'Writing started: {{title}}',
      'to-review': 'Sent to review: {{title}}',
      'back-to-writing': 'Sent back to writing: {{title}}',
      discard: 'Discarded from the editorial plan: {{title}}',
      reopen: 'Reopened as a proposal: {{title}}',
      schedule: 'Planned date updated: {{title}}',
      assign: 'Assignment updated: {{title}}',
      edit: 'Card edited in the editorial plan: {{title}}',
      draft: 'Draft created from the editorial plan: {{title}}',
      publish: 'Published: {{title}}',
      performance: 'Results read from the SEO tool: {{title}}',
      seoUpdate: 'Update from the SEO tool card: {{title}}',
      add: 'Item added by hand to the editorial plan: {{title}}',
      import: 'Cards imported into the editorial plan',
      bridgeOn: 'SEO tool bridge switched on',
      bridgeOff: 'SEO tool bridge switched off',
    },
    edit: {
      button: 'Edit',
      title: 'Edit the item',
      save: 'Save changes',
      seoHelp: 'Fields you edit here win over a future version of the SEO tool card.',
      headingsHelp: 'One H2 per line: title | keyword | angle. Keyword and angle are optional.',
      keywordsHelp: 'Separated by commas or new lines.',
      priorityHelp: '1 is the first to write, 999 the last.',
      priorityInvalid: 'Write a whole number from 1 to 999.',
    },
    new: {
      button: 'New item',
      title: 'New item in the plan',
      titleHelp: 'The working title of the article.',
      titleMissing: 'Write the title.',
      keywordHelp: 'Needed to approve, and to warn you if another item targets the same search.',
      create: 'Add to the plan',
      failed: 'The item was not added',
    },
    discard: {
      title: 'Discard this item?',
      help: 'The item leaves the active plan and no longer counts for cannibalization. You can reopen it any time.',
      note: 'Why you discard it (optional)',
      confirm: 'Discard the item',
    },
    publish: {
      title: 'Mark as published',
      help: 'The linked draft must already be published. The CMS tells the SEO tool the address, and from that day it measures the page. If the tool does not answer the item stays published and you can repeat the notice.',
      url: 'Address of the live article',
      urlInvalid: 'Write a full address starting with https://.',
      urlNeeded: 'The item has no linked draft: write where the article is live.',
      urlOptional: 'Leave empty to use the address of the linked draft.',
      confirm: 'Confirm',
    },
    bridge: {
      open: 'SEO tool bridge',
      title: 'SEO tool bridge',
      help: 'The SEO tool does not tell clients apart: the bridge is switched on site by site, with its own gateway secret. When off, the plan still works with pasted JSON.',
      offTitle: 'The SEO tool bridge is off',
      offHelp: 'You can import cards by pasting their JSON. Whoever administers the client switches the bridge on in the settings.',
      readOnly: 'Only whoever administers the client can change these settings.',
      failed: 'Settings not saved',
      enabled: 'Bridge on for this site',
      baseUrl: 'SEO tool address',
      baseUrlHelp: 'https only, and only a miglioresoftware.com address: the token is never sent elsewhere.',
      seoDomain: 'Site domain in the SEO tool',
      seoDomainHelp: 'How this site is named in the SEO tool, for example example.com.',
      tokenRef: 'Name of the secret holding the token',
      tokenRefHelp: 'The NAME of the gateway secret, for example SEO_TOKEN_ACME. Never the token: whoever runs the gateway sets it.',
      tokenRefInvalid: 'The name starts with SEO_TOKEN_ and uses only capital letters, digits and underscores.',
      tokenOk: 'The secret is configured in the gateway.',
      tokenMissing: 'The secret {{name}} is not set in the gateway yet.',
      save: 'Save',
    },
    import: {
      button: 'Import from the SEO tool',
      pasteJson: 'Paste JSON',
      title: 'Import cards into the plan',
      source: 'Where to import from',
      fromSeo: 'From the SEO tool',
      fromJson: 'Paste JSON',
      preview: 'Preview',
      run: 'Import',
      failed: 'Import failed',
      queueError: 'The SEO tool does not answer',
      useJsonInstead: 'Meanwhile you can paste the JSON of a card.',
      queueEmpty: 'No card in the queue',
      queueEmptyHelp: 'The SEO tool has no card waiting to be written.',
      selectNew: 'Select the new ones and those to update',
      inPlan: 'Already in the plan',
      behind: 'In the plan, to update',
      noResearch: 'No research data',
      invalid: 'Invalid card',
      jsonLabel: 'JSON of the card (or of a list of cards)',
      jsonHelp: 'Paste the JSON of a card as the SEO tool exports it. The research_id field is required: without it the card was not decided on recent search data and is refused.',
      paste: {
        empty: 'Paste some JSON.',
        tooLarge: 'The text is too large: 2 MB at most.',
        notJson: 'This is not valid JSON.',
        notCards: 'The JSON must be a card (an object) or a list of cards.',
        tooMany: 'At most 100 cards at a time.',
      },
      reportTitle: 'Import result',
      previewTitle: 'Preview: nothing was changed',
      doneTitle: 'Import complete',
      summary: '{{created}} new, {{updated}} updated, {{unchanged}} already up to date, {{skipped}} skipped, {{rejected}} refused.',
      keptLocal: 'your value stays',
      keptLocalHelp: 'The SEO tool changed these fields, but you had already edited them here: yours stay.',
      outcome: {
        created: 'New',
        updated: 'Updated',
        unchanged: 'Up to date',
        skipped: 'Skipped',
        rejected: 'Refused',
      },
    },
  },
}
